import './cart-package-product.css';

const PACKAGE_PROPERTY = '_Package product';
const LEGACY_PACKAGE_PROPERTY = 'Main product';

function getPackageParentId(item) {
  const props = item.properties || {};
  return props[PACKAGE_PROPERTY] || props[LEGACY_PACKAGE_PROPERTY] || '';
}

class CartPackageProduct extends HTMLElement {
  constructor() {
    super();
    this.onSelectChange = this.onSelectChange.bind(this);
    this.onToggleChange = this.onToggleChange.bind(this);
    this.stopCartChange = this.stopCartChange.bind(this);
  }

  connectedCallback() {
    this.select = this.querySelector('[data-package-variant]');
    this.toggle = this.querySelector('[data-package-toggle]');
    this.image = this.querySelector('[data-package-image]');
    this.price = this.querySelector('[data-package-price]');
    this.titleEl = this.querySelector('[data-package-title]');
    this.variants = this.readVariants();
    this.parentVariantId = Number(this.dataset.parentVariantId);

    this.addEventListener('change', this.stopCartChange);
    this.select?.addEventListener('change', this.onSelectChange);
    this.toggle?.addEventListener('change', this.onToggleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.stopCartChange);
    this.select?.removeEventListener('change', this.onSelectChange);
    this.toggle?.removeEventListener('change', this.onToggleChange);
  }

  stopCartChange(event) {
    event.stopPropagation();
  }

  readVariants() {
    const script = this.querySelector('[data-package-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  getSelectedVariant() {
    const variantId = Number(this.select?.value);
    return this.variants.find((variant) => Number(variant.id) === variantId) || this.variants[0];
  }

  updatePreview(variant) {
    if (!variant) return;
    if (this.image && variant.image) this.image.src = variant.image;
    if (this.image && variant.title) this.image.alt = variant.title;
    if (this.price && variant.price) this.price.textContent = variant.price;
    if (this.titleEl && variant.title) this.titleEl.textContent = variant.title;
  }

  fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
    };
  }

  getSections() {
    const sections = ['cart-drawer'];
    if (document.getElementById('main-cart-items')) sections.push('cart');
    const gifts = document.getElementById('cartGifts');
    if (gifts && gifts.dataset.id) sections.push(gifts.dataset.id);
    return sections;
  }

  async onSelectChange() {
    const variant = this.getSelectedVariant();
    this.updatePreview(variant);
    if (!this.toggle?.checked || !variant) return;
    await this.swapVariant(variant.id);
  }

  async onToggleChange() {
    const variant = this.getSelectedVariant();
    if (!variant) {
      if (this.toggle) this.toggle.checked = false;
      return;
    }

    if (this.toggle.checked) {
      await this.addVariant(variant.id);
    } else {
      await this.removeLinked();
    }
  }

  async addVariant(variantId) {
    this.classList.add('is-loading');

    try {
      const existing = await this.findLinkedLine();
      if (existing && Number(existing.item.id) === Number(variantId)) return;

      if (existing) {
        await this.swapVariant(variantId, existing);
        return;
      }

      const parsedState = await this.addItem(variantId);
      if (parsedState && !parsedState.status) this.renderCart(parsedState);
      else if (this.toggle) this.toggle.checked = false;
    } catch (error) {
      console.error(error);
      if (this.toggle) this.toggle.checked = false;
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async removeLinked() {
    this.classList.add('is-loading');

    try {
      const existing = await this.findLinkedLine();
      if (!existing) return;

      const parsedState = await this.changeLine(existing.line, 0);
      if (parsedState && !parsedState.status) this.renderCart(parsedState);
      else if (this.toggle) this.toggle.checked = true;
    } catch (error) {
      console.error(error);
      if (this.toggle) this.toggle.checked = true;
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async swapVariant(variantId, existingLine) {
    this.classList.add('is-loading');

    try {
      const existing = existingLine || await this.findLinkedLine();
      if (existing && Number(existing.item.id) === Number(variantId)) return;

      if (existing) {
        const removedState = await this.changeLine(existing.line, 0);
        if (!removedState || removedState.status) return;
      }

      const parsedState = await this.addItem(variantId);
      if (parsedState && !parsedState.status) this.renderCart(parsedState);
    } catch (error) {
      console.error(error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async findLinkedLine() {
    // eslint-disable-next-line no-undef
    const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
    const index = cart.items.findIndex((item) => (
      Number(getPackageParentId(item)) === this.parentVariantId
    ));
    if (index < 0) return null;
    return { item: cart.items[index], line: index + 1 };
  }

  addItem(variantId) {
    const body = JSON.stringify({
      items: [{
        id: Number(variantId),
        quantity: 1,
        properties: { [PACKAGE_PROPERTY]: String(this.parentVariantId) }
      }],
      sections: this.getSections(),
      sections_url: window.location.pathname
    });

    // eslint-disable-next-line no-undef
    return fetch(`${routes.cart_add_url}`, { ...this.fetchConfig(), body })
      .then((response) => response.json());
  }

  changeLine(line, quantity) {
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSections(),
      sections_url: window.location.pathname
    });

    // eslint-disable-next-line no-undef
    return fetch(`${routes.cart_change_url}`, { ...this.fetchConfig(), body })
      .then((response) => response.json());
  }

  getCountFromHtml(html) {
    if (!html) return 0;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let count = 0;
    doc.querySelectorAll('.cart-item .quantity__input').forEach((el) => {
      count += Number(el.value) || 0;
    });
    doc.querySelectorAll('[data-package-quantity]').forEach((el) => {
      count += Number(el.dataset.packageQuantity) || 0;
    });
    return count;
  }

  renderCart(parsedState) {
    const drawerHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    const cartHtml = parsedState.sections && parsedState.sections.cart;
    const cartDrawer = document.querySelector('cart-drawer');
    // On /cart the cart-drawer section renders only GWP markup, so count from the cart section there
    const count = parsedState.item_count == null
      ? this.getCountFromHtml(cartDrawer ? (drawerHtml || cartHtml) : (cartHtml || drawerHtml))
      : parsedState.item_count;

    const counters = document.querySelectorAll('#CartCount span, .cart-count span, #items-count');
    for (let i = 0; i < counters.length; i += 1) {
      counters[i].innerHTML = count;
    }

    if (cartDrawer && drawerHtml) {
      const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const next = doc.querySelector('#CartDrawer');
      const current = document.querySelector('#CartDrawer');
      if (next && current) current.innerHTML = next.innerHTML;
      cartDrawer.classList.toggle('is-empty', count === 0);
    }

    if (cartHtml) {
      const doc = new DOMParser().parseFromString(cartHtml, 'text/html');
      const content = document.querySelector('#main-cart-items .js-content');
      const nextContent = doc.querySelector('.js-content');
      if (content && nextContent) content.innerHTML = nextContent.innerHTML;

      const prices = document.querySelector('.cart-total__prices');
      const nextPrices = doc.querySelector('.cart-total__prices');
      if (prices && nextPrices) prices.innerHTML = nextPrices.innerHTML;
    }

    document.querySelector('cart-gifts')?.init();
    document.dispatchEvent(new CustomEvent('cart-updated', { bubbles: true }));
  }
}

export default CartPackageProduct;
